import { useEffect, useState } from "react";
import { CONDITION_QUIZ_ITEMS, QUIZ_LENGTH } from "../data/conditionQuiz";
import { CONDITIONS, getConditionGuide } from "../helper/conditionGuide";
import "../styles/conditionQuiz.css";

const FALLBACK_IMAGE = "/DEFAULT.PNG";

function scoreMessage(score) {
  const pct = (score / QUIZ_LENGTH) * 100;
  if (pct === 100) return "Perfect — you grade like a pro!";
  if (pct >= 80) return "Great eye — you know our standards well.";
  if (pct >= 60) return "Solid start — review the guide and try again.";
  return "Keep practicing — check the condition guide for our thresholds.";
}

export default function ConditionQuizOverlay({ onClose }) {
  const [phase, setPhase] = useState("intro");
  const [index, setIndex] = useState(0);
  const [guess, setGuess] = useState(null);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);

  const card = CONDITION_QUIZ_ITEMS[index];
  const answered = guess !== null;
  const isCorrect = guess === card?.answer;
  const guide = card ? getConditionGuide(card.answer) : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function startQuiz() {
    setPhase("quiz");
    setIndex(0);
    setGuess(null);
    setScore(0);
    setAnswers([]);
  }

  function submitGuess(condition) {
    if (answered) return;
    const correct = condition === card.answer;
    setGuess(condition);
    if (correct) setScore((s) => s + 1);
    setAnswers((prev) => [
      ...prev,
      { id: card.id, guess: condition, correct, answer: card.answer },
    ]);
  }

  function nextCard() {
    if (index + 1 >= QUIZ_LENGTH) {
      setPhase("results");
      return;
    }
    setIndex((i) => i + 1);
    setGuess(null);
  }

  return (
    <div className="condition-quiz-overlay" role="dialog" aria-modal="true" aria-label="Condition quiz">
      <div className="condition-quiz-backdrop" onClick={onClose} aria-hidden />
      <div className="condition-quiz-panel hide-scrollbar">
        <button type="button" className="condition-quiz-close" onClick={onClose} aria-label="Close quiz">
          ×
        </button>

        {phase === "intro" && (
          <>
            <h1>Condition quiz</h1>
            <p className="condition-quiz-subtitle">
              We grade singles as NM, LP, MP, HP, or DMG. Look at {QUIZ_LENGTH} card
              photos and guess how we would list each one.
            </p>
            <ul className="condition-quiz-rules">
              <li>One photo per round — front or back, as shown</li>
              <li>Tap the condition you think matches our grading</li>
              <li>See instant feedback and our reasoning after each guess</li>
            </ul>
            <button type="button" className="condition-quiz-primary" onClick={startQuiz}>
              Start quiz
            </button>
          </>
        )}

        {phase === "quiz" && card && (
          <>
            <div className="condition-quiz-progress">
              <span>
                Card {index + 1} of {QUIZ_LENGTH}
              </span>
              <div className="condition-quiz-progress-bar">
                <div
                  className="condition-quiz-progress-fill"
                  style={{ width: `${((index + 1) / QUIZ_LENGTH) * 100}%` }}
                />
              </div>
            </div>

            <div className="condition-quiz-image-wrap">
              <img
                src={card.image}
                alt={`Quiz card ${card.id}`}
                className="condition-quiz-image"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = FALLBACK_IMAGE;
                }}
              />
            </div>

            <p className="condition-quiz-prompt">What condition would we give this card?</p>

            <div className="condition-quiz-options">
              {CONDITIONS.map((code) => {
                let className = "condition-quiz-option";
                if (answered) {
                  if (code === card.answer) className += " is-correct";
                  else if (code === guess) className += " is-wrong";
                  else className += " is-muted";
                } else if (code === guess) {
                  className += " is-selected";
                }
                return (
                  <button
                    key={code}
                    type="button"
                    className={className}
                    disabled={answered}
                    onClick={() => submitGuess(code)}
                  >
                    {code}
                  </button>
                );
              })}
            </div>

            {answered && (
              <div
                className={`condition-quiz-feedback ${isCorrect ? "correct" : "incorrect"}`}
              >
                <p className="condition-quiz-feedback-title">
                  {isCorrect ? "Correct!" : `Not quite — we grade this ${card.answer}`}
                </p>
                {guide && (
                  <p className="condition-quiz-feedback-guide">
                    <strong>{guide.name}:</strong> {guide.description}
                  </p>
                )}
                {card.note && (
                  <p className="condition-quiz-feedback-note">{card.note}</p>
                )}
                <button type="button" className="condition-quiz-primary" onClick={nextCard}>
                  {index + 1 >= QUIZ_LENGTH ? "See results" : "Next card"}
                </button>
              </div>
            )}
          </>
        )}

        {phase === "results" && (
          <>
            <h1>Quiz complete</h1>
            <p className="condition-quiz-score">
              {score} / {QUIZ_LENGTH}
            </p>
            <p className="condition-quiz-subtitle">{scoreMessage(score)}</p>

            <ul className="condition-quiz-summary">
              {answers.map((row) => (
                <li key={row.id} className={row.correct ? "ok" : "miss"}>
                  Card {row.id}: you said {row.guess}, answer {row.answer}
                </li>
              ))}
            </ul>

            <div className="condition-quiz-actions">
              <button type="button" className="condition-quiz-primary" onClick={startQuiz}>
                Try again
              </button>
              <button type="button" className="condition-quiz-secondary" onClick={onClose}>
                Back to card
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
